import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, GripVertical, ArrowUp, ArrowDown, FileText, Loader2 } from "lucide-react";

const TYPES = [
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Paragraph" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "yes_no", label: "Yes / No" },
  { value: "dropdown", label: "Dropdown" },
];

export const ApplicationTemplateBuilder = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const withKeys = (arr) => (arr || []).map((q, i) => ({ ...q, _key: q._key || `q_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 8)}` }));

  useEffect(() => {
    api.get("/landlord/application-template")
      .then((r) => setQuestions(withKeys(r.data.questions)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addQuestion = () => setQuestions((q) => [...q, { id: "", label: "", type: "short_text", required: false, options: [], _key: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }]);
  const update = (i, patch) => setQuestions((q) => q.map((item, idx) => (idx === i ? { ...item, ...patch } : item)));
  const remove = (i) => setQuestions((q) => q.filter((_, idx) => idx !== i));
  const move = (i, dir) => setQuestions((q) => {
    const arr = [...q];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  const save = async () => {
    for (const q of questions) {
      if (!q.label.trim()) { toast.error("Every question needs a label."); return; }
      if (q.type === "dropdown" && !(q.options || []).filter((o) => o.trim()).length) {
        toast.error(`Add at least one option for "${q.label}".`); return;
      }
    }
    setSaving(true);
    try {
      const payload = questions.map((q) => ({
        id: q.id || "", label: q.label.trim(), type: q.type, required: !!q.required,
        options: q.type === "dropdown" ? (q.options || []).map((o) => o.trim()).filter(Boolean) : [],
      }));
      const { data } = await api.put("/landlord/application-template", { questions: payload });
      setQuestions(withKeys(data.questions));
      toast.success("Application template saved");
    } catch (e) {
      toast.error("Could not save template");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="bg-white border border-line rounded-xl p-6 text-sm text-mute">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-4" data-testid="app-template-builder">
      <div className="bg-white border border-line rounded-xl p-6">
        <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-forest" /><h2 className="text-lg font-heading font-medium text-ink">Rental Application Form</h2></div>
        <p className="text-sm text-mute mt-1">Build the questions renters answer when they apply. Every application always collects these standard fields: <span className="font-medium text-ink">full name, move-in date, employer, employer address, supervisor name, supervisor phone, annual income and monthly income</span> — add anything else you need below. This form is used on all your listings.</p>

        <div className="mt-4 space-y-3">
          {questions.length === 0 && <p data-testid="template-empty" className="text-sm text-mute">No custom questions yet. Only the built-in fields will be asked.</p>}
          {questions.map((q, i) => (
            <div key={q._key} data-testid={`template-q-${i}`} className="border border-line rounded-lg p-3 bg-bone/30">
              <div className="flex items-start gap-2">
                <GripVertical className="h-4 w-4 text-mute mt-2.5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <Input data-testid={`template-q-label-${i}`} value={q.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Question label (e.g. Do you have pets?)" className="border-line" />
                  <div className="flex flex-wrap items-center gap-2">
                    <select data-testid={`template-q-type-${i}`} value={q.type} onChange={(e) => update(i, { type: e.target.value })} className="border border-line rounded-lg px-2.5 py-1.5 text-sm bg-white">
                      {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <label className="flex items-center gap-1.5 text-sm text-ink">
                      <input type="checkbox" data-testid={`template-q-required-${i}`} checked={q.required} onChange={(e) => update(i, { required: e.target.checked })} /> Required
                    </label>
                  </div>
                  {q.type === "dropdown" && (
                    <Input data-testid={`template-q-options-${i}`} value={(q.options || []).join(", ")} onChange={(e) => update(i, { options: e.target.value.split(",") })} placeholder="Comma-separated options (e.g. Yes, No, Sometimes)" className="border-line text-sm" />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button data-testid={`template-q-up-${i}`} onClick={() => move(i, -1)} className="text-mute hover:text-ink p-1"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button data-testid={`template-q-down-${i}`} onClick={() => move(i, 1)} className="text-mute hover:text-ink p-1"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button data-testid={`template-q-remove-${i}`} onClick={() => remove(i)} className="text-red-500 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button data-testid="template-add-q" onClick={addQuestion} className="border border-forest text-forest px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest/5 flex items-center gap-1.5"><Plus className="h-4 w-4" /> Add question</button>
          <button data-testid="template-save" onClick={save} disabled={saving} className="bg-forest text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover flex items-center gap-1.5 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save template</button>
        </div>
      </div>
    </div>
  );
};
